import React, { useCallback, useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import _get from 'lodash/get';
import HttpStatus from 'http-status-codes';

import { getWebpackEnvVar } from '@trqk/utils';
import {
    Button,
    IconMusicNotes,
    IconPlus,
    IconX,
    ValidatedInput,
    LoadingEllipses,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { validateInputs } from '@trqk/hooks';

import { useTokenModal, useToast } from '../../../hooks';

const { ADD_INVITATION, GET_SESSION_CURRENT_USER } = queryStrings;

const getGrammar = (
    inviteCount: number,
): { invitesSingularOrPlural: string; wasOrWere: string } => {
    const invitesSingularOrPlural = inviteCount === 1 ? 'invite' : 'invites';
    const wasOrWere = inviteCount === 1 ? 'was' : 'were';

    return {
        invitesSingularOrPlural,
        wasOrWere,
    };
};

type HandleInvitesSent = (collaborators: Collaborator[]) => void;

const inputIds = {
    firstName: 'inviteCollaboratorsFirstName',
    lastName: 'inviteCollaboratorsLastName',
    email: 'inviteCollaboratorsEmail',
};

interface Props extends ModalContentProps<Record<string, never>> {
    handleInvitesSent?: HandleInvitesSent | null;
    trqkSongId: Maybe<string>;
    title: Maybe<string>;
}

export const InviteCollaborators = ({
    handleInvitesSent = null,
    trqkSongId,
    title,
    remove,
}: Props): JSX.Element => {
    const invitationsUrl = getWebpackEnvVar('web', 'INVITATIONS_URL');
    const { getToken } = useTokenModal();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [didSubmit, setDidSubmit] = useState(false);
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);
    const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
    const [invitesSent, setInvitesSent] = useState(0);
    const [invitesFailed, setInvitesFailed] = useState(0);
    const { data: currentUserData } = useQuery(GET_SESSION_CURRENT_USER);
    const toast = useToast();
    const [addInvitation, { loading }] = useMutation(ADD_INVITATION, {
        onCompleted: data => {
            const status = _get(data, ['addInvitation', 'response', 'status']);

            if (status === HttpStatus.OK) {
                setInvitesSent(invitesSent + 1);
                remove({
                    canceled: false,
                    success: true,
                });
            } else {
                setInvitesFailed(invitesFailed + 1);
                remove({
                    canceled: false,
                    success: false,
                });
            }
        },
        onError: () => {
            setInvitesFailed(invitesFailed + 1);
            remove({
                canceled: false,
                success: false,
            });
        },
    });

    useEffect(() => {
        const totalInvitesHandled = invitesSent + invitesFailed;

        if (
            totalInvitesHandled > 0 &&
            totalInvitesHandled === collaborators.length
        ) {
            if (invitesSent > 0) {
                const sentGrammar = getGrammar(invitesSent);

                toast.add({
                    kind: 'info-banner',
                    content: `${invitesSent} ${sentGrammar.invitesSingularOrPlural} ${sentGrammar.wasOrWere} sent!`,
                });
            }

            if (invitesFailed > 0) {
                const failedGrammar = getGrammar(invitesFailed);

                toast.add({
                    content: `We were unable to send ${invitesFailed} ${failedGrammar.invitesSingularOrPlural}. Please try again.`,
                    kind: 'warning-banner',
                });
            }
            if (handleInvitesSent) {
                handleInvitesSent(collaborators);
            }

            setCollaborators([]);
        }
    }, [invitesSent, invitesFailed, collaborators, handleInvitesSent, toast]);

    const validate = useCallback(
        () =>
            validateInputs([
                {
                    inputId: inputIds.firstName,
                    label: 'First name',
                    required: true,
                    type: 'name',
                    value: firstName,
                },
                {
                    inputId: inputIds.lastName,
                    label: 'Last name',
                    required: true,
                    type: 'name',
                    value: lastName,
                },
                {
                    inputId: inputIds.email,
                    label: 'Email',
                    required: true,
                    type: 'email',
                    value: email,
                },
            ]),
        [firstName, lastName, email],
    );

    useEffect(() => {
        if (didSubmit) {
            const validationResults = validate();

            setValidationOutputs(validationResults.validationOutputs);
        }
    }, [didSubmit, validate, email, firstName, lastName]);

    const handleSubmit = () => {
        const validationResults = validate();

        if (!validationResults.hasValidationErrors) {
            setCollaborators(
                (prevCollaborators: Collaborator[]): Collaborator[] =>
                    prevCollaborators.concat([
                        {
                            email,
                            firstName,
                            lastName,
                        },
                    ]),
            );

            setFirstName('');
            setLastName('');
            setEmail('');
            setDidSubmit(false);
        } else {
            setDidSubmit(true);
            setValidationOutputs(validationResults.validationOutputs);
        }
    };

    return (
        <div className="bg-white rounded">
            <div className="pt-6 pr-6 flex justify-end border-l-4 border-primary-500 rounded-tl">
                <IconX
                    width={20}
                    fillColor="tertiary-400"
                    handleClick={remove}
                />
            </div>
            <div className="px-10 pb-4 flex 0 content-start border-l-4 border-primary-500">
                <div className="flex-auto pt-1">
                    <h3>Invite Collaborators</h3>
                    <div className="flex items-center mt-2">
                        <IconMusicNotes
                            addClasses="mr-2 bg-primary-100 p-1 rounded-full"
                            width={30}
                            fillColor="primary-500"
                        />
                        <div className="font-semibold">{title}</div>
                    </div>
                    <div className="text-sm py-4">
                        Adding co-writers or publishers helps Trqk provide
                        <br />
                        you with more detailed royalty analytics.
                    </div>
                </div>
            </div>
            <div className="bg-offwhite py-5 px-10">
                <ValidatedInput
                    addClasses="my-3"
                    inputId={inputIds.firstName}
                    icon="name"
                    type="name"
                    placeholder="First name"
                    value={firstName}
                    handleChange={setFirstName}
                    validationOutputs={validationOutputs}
                />
                <ValidatedInput
                    addClasses="my-3"
                    inputId={inputIds.lastName}
                    icon="name"
                    type="name"
                    placeholder="Last name"
                    value={lastName}
                    handleChange={setLastName}
                    validationOutputs={validationOutputs}
                />
                <ValidatedInput
                    addClasses="my-3"
                    inputId={inputIds.email}
                    icon="email"
                    type="email"
                    placeholder="Email"
                    value={email}
                    handleChange={setEmail}
                    validationOutputs={validationOutputs}
                />
            </div>
            <div className="px-10 py-4">
                <button type="button" onClick={handleSubmit}>
                    <IconPlus fillColor="secondary-600" width={20} />
                    <span className="ml-2 text-secondary-600 font-medium underline">
                        Add Collaborator
                    </span>
                </button>
            </div>
            <div className="overflow-y-scroll h-24 px-10">
                {collaborators.map(
                    (collaborator: Collaborator): JSX.Element => (
                        <div
                            key={collaborator.email}
                            className="py-1 px-2 text-sm border border-quarternary-100 rounded"
                        >
                            <span className="font-semibold">
                                {`${collaborator.firstName} ${collaborator.lastName}`}
                            </span>
                            {` | ${collaborator.email}`}
                        </div>
                    ),
                )}
            </div>
            <div className="p-4 mx-6 my-3 flex justify-end items-center">
                <div className="w-1/2">
                    <Button
                        disabled={collaborators.length < 1}
                        color="primary"
                        handleClick={(): void => {
                            const inviteSenderName = _get(currentUserData, [
                                'session',
                                'currentUser',
                                'name',
                            ]);
                            const inviteSenderId = _get(currentUserData, [
                                'session',
                                'currentUser',
                                'id',
                            ]);

                            collaborators.forEach(
                                (collaborator: Collaborator): void => {
                                    getToken((token: string): void => {
                                        addInvitation({
                                            variables: {
                                                token,
                                                collaborations: trqkSongId
                                                    ? [trqkSongId]
                                                    : [],
                                                invitationsUrl,
                                                inviteeEmail:
                                                    collaborator.email,
                                                inviteeFirstName:
                                                    collaborator.firstName,
                                                inviteeLastName:
                                                    collaborator.lastName,
                                                inviteSenderName,
                                                inviteSenderId,
                                            },
                                        });
                                    });
                                },
                            );
                        }}
                    >
                        <div className="text-center">
                            {!loading && (
                                <div>{`Send (${collaborators.length}) Invites`}</div>
                            )}
                            {loading && <LoadingEllipses width={40} />}
                        </div>
                    </Button>
                </div>
            </div>
        </div>
    );
};
